






















































































































































import firebase from "firebase/app";
import "firebase/auth";
import { Component, Vue } from "vue-property-decorator";
import { domain, User, VueForm } from "@/domain";
import { MfaRequiredError } from "@/domain/backend/auth";
import * as util from "@/util";
import * as uiUtil from "@/uiUtil";
import { createRecaptchaVerifier } from "@/util/recaptcha";
import QuickSnack from "@/components/QuickSnack.vue";
import Password from "@/components/Password.vue";
import { getUserStartPage } from "@/modules/router";

@Component({
  components: { QuickSnack, Password }
})
export default class AuthLoginForm extends Vue {
  // Status
  snackbar = "";
  isLoading = false;
  step = "login";

  // Login form
  email = "";
  password = "";
  rememberMe = false;
  validLogin = true;
  emailRules = [util.rules.required(this.lang.requiredEmailAlert), util.rules.email(this.lang.validEmailAlert)];

  // Forgot form
  forgotEmail = "";
  validForgot = true;

  // Phone MFA sign-in challenge
  mfaRecaptchaContainerId = "login-mfa-recaptcha";
  mfaRecaptchaVerifier: firebase.auth.RecaptchaVerifier | null = null;
  mfaResolver: firebase.auth.MultiFactorResolver | null = null;
  mfaVerificationId = "";
  mfaCode = "";

  get lang() {
    return this.$store.state.lang.data;
  }

  get currentUser(): User | null {
    return this.$store.state.currentUser;
  }

  get currentStep(): string {
    if (this.step === "login") {
      return this.currentUser ? "login_existing" : "login_new";
    }
    return this.step;
  }

  get currentTitle(): string {
    switch (this.currentStep) {
      case "login_new":
        return this.lang.authTitle1;
      case "login_existing":
        return this.lang.authTitle1;
      case "forgot1":
        return this.lang.rememberPassword;
      case "forgot2":
        return this.lang.rememberPassword;
      case "mfa":
        return this.lang.verificationCode;
      default:
        return this.step;
    }
  }

  cmdCurrentLogin() {
    uiUtil.routePush(this, getUserStartPage());
  }

  async cmdAnotherLogin() {
    this.isLoading = true;
    try {
      await this.$store.dispatch("logout");
    } catch (e) {
      util.logError("Error login out", e);
    }
    this.isLoading = false;
  }

  async cmdLogin() {
    if ((this.$refs.loginForm as VueForm).validate()) {
      this.isLoading = true;
      try {
        // Try to login
        await this.$store.dispatch("login", {
          email: this.email,
          password: this.password,
          rememberMe: this.rememberMe
        });
        this.isLoading = false;
        uiUtil.routePush(this, getUserStartPage());
      } catch (err) {
        if (err instanceof MfaRequiredError) {
          this.mfaResolver = err.resolver;
          await this.startMfaChallenge();
        } else {
          this.isLoading = false;
          this.snackbar = uiUtil.toErrorMsg(err, this.lang.errorMsgLogin);
        }
      }
    } else {
      this.snackbar = this.lang.errorMsgForm;
    }
  }

  // Discards the previous widget (if any) before rendering a new one - reusing the
  // same container without clearing throws "reCAPTCHA has already been rendered".
  getFreshRecaptchaVerifier(): firebase.auth.RecaptchaVerifier {
    this.mfaRecaptchaVerifier?.clear();
    this.mfaRecaptchaVerifier = createRecaptchaVerifier(this.mfaRecaptchaContainerId);
    return this.mfaRecaptchaVerifier;
  }

  async startMfaChallenge() {
    const resolver = this.mfaResolver;
    if (!resolver) {
      return;
    }
    this.step = "mfa";
    await this.$nextTick();
    try {
      const verifier = this.getFreshRecaptchaVerifier();
      this.mfaVerificationId = await domain.backend.startMfaSignInChallenge(resolver, verifier);
      this.snackbar = this.lang.mfaCodeSentLegend;
    } catch (err) {
      util.logError("Error iniciando desafío MFA", err);
      this.snackbar = uiUtil.toMfaErrorMsg(err, this.lang);
    } finally {
      this.isLoading = false;
    }
  }

  async cmdVerifyMfa() {
    if (!this.mfaResolver || !this.mfaVerificationId) {
      return;
    }
    this.isLoading = true;
    try {
      await domain.backend.completeMfaSignIn(this.mfaResolver, this.mfaVerificationId, this.mfaCode);
      this.isLoading = false;
      uiUtil.routePush(this, getUserStartPage());
    } catch (err) {
      util.logError("Error verificando MFA", err);
      this.isLoading = false;
      this.snackbar = uiUtil.toMfaErrorMsg(err, this.lang);
    }
  }

  cmdCancelMfa() {
    this.mfaResolver = null;
    this.mfaVerificationId = "";
    this.mfaCode = "";
    this.step = "login";
  }

  cmdForgot() {
    if ((this.$refs.forgotForm as VueForm).validate()) {
      this.isLoading = true;
      domain.backend
        .resetPassword(this.forgotEmail)
        .then(() => {
          this.isLoading = false;
          this.goToStep("forgot2");
        })
        .catch((err) => {
          this.isLoading = false;
          this.snackbar = uiUtil.toErrorMsg(err, this.lang.errorMsgEmail);
        });
    } else {
      this.snackbar = this.lang.errorMsgForm;
    }
  }

  goToStep(name: string) {
    this.step = name;
  }
}
